import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Service } from '@turizi/shared';
import { Button, Loader, ActionIcon, Tooltip } from '@mantine/core';
import { useService, useUpdateService } from '@/hooks/api-hooks';
import { IconPlus, IconTrash, IconArrowsRightLeft, IconRefresh } from '@tabler/icons-react';
import ServicesListWithFilters from './services-list-with-filters';
import ServiceCardCatalog from './service-card-catalog';
import { notify } from '@/lib/notify';
import CardModal from './card-modal';
import { ServiceTitle } from './services/service-title';

interface Props {
    service: Service;
}

export default function MirrorServiceSelector({ service }: Props) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);

    const updateServiceMutation = useUpdateService();
    const {
        data: linkedServiceData,
        isLoading: loadingLinkedService
    } = useService(service.linked_service_id);

    const linkedService = linkedServiceData?.data || (service.linked_service_id ? service.linked_service : null);

    const handleSelect = async (selectedService: Service) => {
        if (selectedService.id === service.id) {
            notify.error(t('cannot_link_service_to_itself'), '');
            return;
        }

        if (service.linked_service_id === selectedService.id) {
            setOpened(false);
            return;
        }

        try {
            // Clean up previous link for this service if it had a different linked service
            if (service.linked_service_id && service.linked_service_id !== selectedService.id) {
                await updateServiceMutation.mutateAsync({
                    id: service.linked_service_id,
                    linked_service_id: null
                });
            }

            // Clean up previous link for selected service if it was linked to something else
            if (selectedService.linked_service_id && selectedService.linked_service_id !== service.id) {
                await updateServiceMutation.mutateAsync({
                    id: selectedService.linked_service_id,
                    linked_service_id: null
                });
            }

            // Link mutually
            await updateServiceMutation.mutateAsync({
                id: service.id,
                linked_service_id: selectedService.id
            });
            await updateServiceMutation.mutateAsync({
                id: selectedService.id,
                linked_service_id: service.id
            });

            notify.success(t('service_linked_successfully'), '');
            setOpened(false);
        } catch {
            // Error handled by mutation
        }
    };

    const handleRemove = async () => {
        try {
            const previousLinkedId = service.linked_service_id;
            await updateServiceMutation.mutateAsync({
                id: service.id,
                linked_service_id: null
            });
            if (previousLinkedId) {
                await updateServiceMutation.mutateAsync({
                    id: previousLinkedId,
                    linked_service_id: null
                });
            }
            notify.success(t('service_link_removed'), '');
        } catch {
            // Error handled by mutation
        }
    };

    return (
        <div className="solid-box-border space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <span className="text-md font-bold text-gray-900">
                        {t('linked_service')}
                    </span>
                </div>
                {linkedService ? (
                    <Button
                        size="xs"
                        variant="default"
                        radius="xl"
                        onClick={() => setOpened(true)}
                        leftSection={<IconRefresh size={14} />}
                        loading={updateServiceMutation.isPending}
                    >
                        {t('change_linked_service')}
                    </Button>
                ) : (
                    <Button
                        size="xs"
                        color="black"
                        radius="xl"
                        onClick={() => setOpened(true)}
                        leftSection={<IconPlus size={14} />}
                        loading={updateServiceMutation.isPending}
                    >
                        {t('select_linked_service')}
                    </Button>
                )}
            </div>

            <div className="text-xxs text-gray-400">
                {t('linked_service_description')}
            </div>

            {/* List or States */}
            {loadingLinkedService ? (
                <div className="flex justify-center p-6">
                    <Loader size="sm" />
                </div>
            ) : linkedService ? (
                <div className="flex items-center justify-between gap-2 p-2 bg-white rounded-xl border border-gray-200 shadow-2xs">
                    <div className="flex-1 min-w-0">
                        <ServiceCardCatalog
                            service={linkedService}
                            iconSize="xs"
                            showProviders={true}
                            showSoldBy={true}
                        />
                    </div>
                    <Tooltip label={t('unlink_service')} position="left" withArrow>
                        <ActionIcon
                            color="red"
                            variant="subtle"
                            size="md"
                            radius="md"
                            onClick={handleRemove}
                            loading={updateServiceMutation.isPending}
                        >
                            <IconTrash size={16} />
                        </ActionIcon>
                    </Tooltip>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-6 px-4 border border-dashed border-gray-200 rounded-xl bg-gray-50/50 text-center">
                    <IconArrowsRightLeft size={28} className="text-gray-300 mb-1.5" stroke={1.5} />
                    <div className="text-xs font-medium text-gray-500">
                        {t('no_linked_service')}
                    </div>
                </div>
            )}

            <CardModal
                opened={opened}
                onClose={() => setOpened(false)}
                header={(
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full pr-4">
                        <div className="flex items-center gap-2.5">
                            <div className="p-2 rounded-lg bg-gray-100 text-gray-800 shrink-0">
                                <IconArrowsRightLeft size={18} stroke={1.75} />
                            </div>
                            <div>
                                <div className="text-base font-bold text-gray-900 leading-tight">
                                    {t('select_linked_service')}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {t('linked_service_description')}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-md">
                            <ServiceTitle service={service} />
                        </div>
                    </div>
                )}
            >
                <ServicesListWithFilters
                    onSelect={handleSelect}
                />
            </CardModal>
        </div>
    );
}
